<style lang="scss">
.app-container {
    overflow: hidden;
}
</style>

<style lang="scss" scoped>
.map-cards {
    position: absolute;
    z-index: 1;
    height: 100%;
    overflow-y: scroll;
    padding: 45px;
    max-width: 580px;
    width: auto;
    &::-webkit-scrollbar {
        display: none;
    }
}
.map-legend {
    position: absolute;
    right: 45px;
    top: 45px;
    z-index: 1;
    min-width: 150px;
}
.multiselect {
    width: 200px;
    font-size: 12px;
    z-index: 2;
}
</style>

<template>
    <div>

        <!-- Filter bar -->
        <!-- Contains a list of all filters -->
        <!-- Pickuplocation, Deliverylocation, Vehicle -->
        <div class="filter-bar d-flex" @click="filter = ''">
            <i-filter class="i-20 mr-20 mt-10"></i-filter>
            <div class="search-btn fs-12" :class="{ active : filter === 'type' }" @click.stop="filter = 'type'">
                {{ $t('shipments.filters.shipments') }}
                <filter-shipment-type v-show="filter === 'type'" @update="typeFilterChanged"></filter-shipment-type>
            </div>

            <div class="search-btn fs-12" :class="{ active : filter === 'pickup' || ( pickup.end  || pickup.start  || pickup.location) }" @click.stop="filter = 'pickup'">
                {{ $t('shipments.filters.pickup') }}
                <filter-location v-show="filter === 'pickup'" @update="pickupFilterChanged" color="green" :label="$t('shipments.filters.showpickuplabel')" :inputLabel="$t('shipments.filters.locationplaceholder')" :dateLabel="$t('shipments.filters.pickupdatelabel')"></filter-location>
            </div>

            <div class="search-btn fs-12" :class="{ active : filter === 'drop' || ( drop.end || drop.start || drop.location) }" @click.stop="filter = 'drop'">
                {{ $t('shipments.filters.delivery') }}
                <filter-location v-show="filter === 'drop'" @update="dropFilterChanged" color="orange" :label="$t('shipments.filters.showdeliverylabel')" :inputLabel="$t('shipments.filters.locationplaceholder')" :dateLabel="$t('shipments.filters.deliverydatelabel')"></filter-location>
            </div>

            <div class="search-btn fs-12" :class="{ active : filter === 'vehicle' }" @click.stop="filter = 'vehicle'">
                {{ $t('shipments.filters.dimensions') }}
                <filter-vehicle v-show="filter === 'vehicle'" @update="vehicleFilterChanged"></filter-vehicle>
            </div>

            <div class="ml-auto fs-12">
                <multiselect v-model="order" :preselect-first="true" :options="orderFilters" label="name" track-by="name" :hide-selected="true"></multiselect>
            </div>
        </div>

        <!-- Search results -->
        <!-- Contains a map and a list of search results -->
        <div class="can-blur row row--45 position-relative" :class="{ blur : ['drop','pickup','vehicle', 'type'].includes(filter)}" @click="filter = ''">
            <div v-if="newshipments.length > 0" class="map-cards">
                <template v-if="filteredShipment.length > 0">
                    <div class="mb-45" @mouseenter="showSuggestedRoute = shipment.id" @mouseleave="showSuggestedRoute = false" v-for="(shipment, key) in filteredShipment" :key="shipment.id">
                        <shipment-card :item="shipment" :type="'search-detail'" :offer="retrieveOffer(shipment)" shipmenttype="new"></shipment-card>
                    </div>
                </template>
                <template v-else>
                    <box>
                        <template slot="content">
                            <div class="d-flex">
                                <i-empty-search class="mr-30"></i-empty-search>
                                <div class="my-auto">
                                    <p class="mb-0 fs-20 text-secondary fw-semi-bold">{{ $t('shipments.filters.notfound') }}</p>
                                    <p class="fs-12 mb-0">{{ $t('shipments.filters.notfounddisclaimer') }}</p>
                                </div>
                            </div>
                        </template>
                    </box>
                </template>
            </div>
            <div v-else class="map-cards">
                <box>
                    <template slot="content">
                        <div class="d-flex">
                            <i-empty-search class="mr-30"></i-empty-search>
                            <div class="my-auto">
                                <p class="mb-0 fs-20 text-secondary fw-semi-bold">{{ $t('shipments.filters.notavailable') }}</p>
                                <p class="fs-12 mb-0">{{ $t('shipments.filters.notavailabledisclaimer') }}</p>
                            </div>
                        </div>
                    </template>
                </box>
            </div>
            <box class="map-legend" :padding="15">
                <template slot="content">
                    <p class="mb-15 fs-12">{{ $t('shipments.showonmap') }}</p>
                    <div class="custom-control custom-checkbox mb-15">
                        <input v-model="showPickup" type="checkbox" class="custom-control-input" id="showPickup">
                        <label class="custom-control-label fs-12 my-auto fw-normal text-secondary" for="showPickup">{{ $t('shipments.pickupmarkers') }}</label>
                    </div>
                    <div class="custom-control custom-checkbox mb-15">
                        <input v-model="showDrop" type="checkbox" class="custom-control-input" id="showDrop">
                        <label class="custom-control-label fs-12 my-auto fw-normal text-secondary" for="showDrop">{{ $t('shipments.deliverymarkers') }}</label>
                    </div>
                    <div class="custom-control custom-checkbox">
                        <input v-model="showRoutes" type="checkbox" class="custom-control-input" id="showRoutes">
                        <label class="custom-control-label fs-12 my-auto fw-normal text-secondary" for="showRoutes">{{ $t('shipments.suggestedroutes') }}</label>
                    </div>
                </template>
            </box>
            <GmapMap ref="gmap" :center="center" :zoom="11" map-type-id="roadmap" :options="options" style="width: 100%; height: calc(100vh - 30px - 80px - 60px);">
                <template v-for="shipment in filteredShipment">
                    <gmap-polyline v-if="(showSuggestedRoute === shipment.id) || showRoutes" :key="shipment.id" :path="decodeRoute(shipment.route.points, true)" :options="{geodesic:true, strokeColor:'#48616f', strokeOpacity: 0, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 }, offset: '0', repeat: '25px' }]}">
                    </gmap-polyline>
                </template>
                <gmap-marker @click="showSuggestedRoute = marker.shipment_id" v-for="(marker, key) in markers" :key="key" :position="marker.position" :icon="marker.icon"></gmap-marker>
            </GmapMap>
        </div>

    </div>
</template>

<script>
// TODO: bij hoveren over ophaal / levermarker ook route tonen --> hover werkt precies niet. Maar click wel
// @jan hover event is niet beschikbaar voor markers. 

const mapstyle = require("@/assets/mapstyles.json");

import { mapGetters, mapActions } from "vuex";
import ShipmentCard from "@/components/shipments/card";
import {
    FilterVehicle,
    FilterLocation,
    FilterShipmentType
} from "@/components/filters/_index";
import Multiselect from "vue-multiselect";

export default {
    name: "search-shipments",
    components: {
        ShipmentCard,
        FilterVehicle,
        FilterLocation,
        FilterShipmentType,
        Multiselect
    },
    data() {
        return {
            filter: "",
            pickup: {
                show: true,
                location: false,
                range: null,
                start: false,
                end: false
            },
            drop: {
                show: true,
                location: false,
                range: null,
                start: false,
                end: false
            },
            vehicle: {
                width: false,
                depth: false,
                height: false,
                weight: false,
                extras: []
            },
            type: false,
            markers: [],
            order: "",
            orderFilters: [
                {
                    name: this.$t("shipments.sort.most_recent"),
                    slug: "most_recent"
                },
                { name: this.$t("shipments.sort.oldest"), slug: "oldest" }
            ],
            showPickup: true,
            showDrop: true,
            showRoutes: false,
            center: { lat: 50.93069, lng: 5.33248 },
            options: {
                styles: mapstyle,
                scrollwheel: false,
                disableDefaultUI: true,
                zoomControl: true
            },
            showSuggestedRoute: false
        };
    },
    mounted() {
        let self = this;

        /* 
        | --------------
        | Query all shipments for a given user
        | --------------
        */
        this.$gmapApiPromiseLazy().then(() => {
            this.$refs.gmap.$mapPromise.then(() => {
                self.searchShipments();
            });
        });
    },
    methods: {
        ...mapActions(["searchShipments"]),

        /* 
        | --------------
        | Initialise google markers
        | --------------
        */
        initMarker(shipment) {
            let pickup_marker = {
                position: {
                    lat: shipment.pickup_location.lat,
                    lng: shipment.pickup_location.lng
                },
                icon: this.getMarker("green"),
                shipment_id: shipment.id
            };
            let destination_marker = {
                position: {
                    lat: shipment.delivery_location.lat,
                    lng: shipment.delivery_location.lng
                },
                icon: this.getMarker("orange"),
                shipment_id: shipment.id
            };

            if (this.showPickup) this.markers.push(pickup_marker);
            if (this.showDrop) this.markers.push(destination_marker);
        },

        /* 
        | --------------
        | Zoom map to show all markers
        | --------------
        */
        fitBounds() {
            if (this.$refs.gmap.$mapObject) {
                let bounds = new google.maps.LatLngBounds();
                this.markers.forEach(marker => {
                    bounds.extend(marker.position);
                });
                this.$refs.gmap.fitBounds(bounds);
                this.center = {
                    lat: bounds.getCenter().lat(),
                    lng: bounds.getCenter().lng()
                };
            }
        },

        /* 
        | --------------
        | Filter all markers
        | --------------
        */
        filterMarkers() {
            this.markers = [];
            if (this.showRoutes || this.showPickup || this.showDrop) {
                this.filteredShipment.forEach(shipment => {
                    this.initMarker(shipment);
                    this.$nextTick().then(() => {
                        this.fitBounds();
                    });
                });
            } else {
                this.center = { lat: 50.93069, lng: 5.33248 };
            }
        },

        /* 
        | --------------
        | Order a given set of shipments
        | --------------
        */
        orderShipments(shipments) {
            // Order by updated_at asc
            if (this.order.slug === "most_recent")
                shipments.sort(function(a, b) {
                    return new Date(b.created_at) - new Date(a.created_at);
                });

            // Order by updated_at desc
            if (this.order.slug === "oldest")
                shipments.sort(function(a, b) {
                    return new Date(a.created_at) - new Date(b.created_at);
                });

            return shipments;
        },

        /* 
        | --------------
        | Handle events when a filter has changed
        | --------------
        */
        typeFilterChanged(type) {
            this.type = type;
        },
        vehicleFilterChanged(vehicle) {
            this.vehicle = vehicle;
        },
        pickupFilterChanged(pickup) {
            this.pickup = pickup;
        },
        dropFilterChanged(drop) {
            this.drop = drop;
        },
        retrieveOffer(shipment) {
            return shipment.offer_made ? shipment.offers[0] : {};
        }
    },
    computed: {
        ...mapGetters(["newshipments", "user"]),
        filteredShipment() {
            let self = this;
            self.markers = [];
            let ul = this.newshipments.filter(shipment => {
                let is_allowed = true;

                // 1. Check if the pickup date matches the shipment pickup date
                if (
                    self.pickup.start &&
                    self.pickup.end &&
                    !self.$moment
                        .unix(shipment.pickup_date_from)
                        .isBetween(
                            self.$moment(self.pickup.start),
                            self.$moment(self.pickup.end)
                        )
                ) {
                    is_allowed = false;
                }

                // 2. Check if the drop date matches the shipment drop date
                if (
                    self.drop.start &&
                    self.drop.end &&
                    !self.$moment
                        .unix(shipment.delivery_date_till)
                        .isBetween(
                            self.$moment(self.drop.start),
                            self.$moment(self.drop.end)
                        )
                ) {
                    is_allowed = false;
                }

                // 3. check location range
                if (self.pickup.location.address && self.pickup.range) {
                    let distance = this.getDistanceFromLatLonInKm(
                        shipment.pickup_location.lat,
                        shipment.pickup_location.lng,
                        self.pickup.location.coord.lat,
                        self.pickup.location.coord.lng
                    );
                    if (distance > self.pickup.range) is_allowed = false;
                }

                // 4. check location range
                if (self.drop.location.address && self.drop.range) {
                    let distance = this.getDistanceFromLatLonInKm(
                        shipment.drop_location.lat,
                        shipment.drop_location.lng,
                        self.drop.location.coord.lat,
                        self.drop.location.coord.lng
                    );
                    if (distance > self.drop.range) is_allowed = false;
                }

                // 5.  Check vehicle filter
                if (
                    self.vehicle.width &&
                    shipment.packages.findIndex(p => {
                        return p.width <= self.vehicle.width;
                    }) === -1
                ) {
                    is_allowed = false;
                }

                if (
                    self.vehicle.depth &&
                    shipment.packages.findIndex(p => {
                        return p.length <= self.vehicle.depth;
                    }) === -1
                ) {
                    is_allowed = false;
                }

                if (
                    self.vehicle.weight &&
                    shipment.packages.findIndex(p => {
                        return p.weight <= self.vehicle.weight;
                    }) === -1
                ) {
                    is_allowed = false;
                }

                if (
                    self.vehicle.height &&
                    shipment.packages.findIndex(p => {
                        return p.height <= self.vehicle.height;
                    }) === -1
                ) {
                    is_allowed = false;
                }

                if (
                    self.vehicle.extras.length > 0 &&
                    shipment.packages.findIndex(p => {
                        return (
                            p.tags.findIndex(t => {
                                return self.vehicle.extras.includes(
                                    this.getExtraSlug(t)
                                );
                            }) !== -1
                        );
                    }) === -1
                ) {
                    is_allowed = false;
                }

                if (self.type.shipmentsWithoutOffers && shipment.offer_made) {
                    is_allowed = false;
                }
                if (
                    self.type.shipmentsWithMyOffers &&
                    shipment.offers.findIndex(o => {
                        return o.user_id === self.user.id;
                    })
                ) {
                    is_allowed = false;
                }

                if (is_allowed) self.initMarker(shipment);
                if (is_allowed) return shipment;
            });

            return this.orderShipments(ul);
        }
    },
    watch: {
        newshipments: function() {
            this.filterMarkers();
        },
        showPickup: function() {
            this.filterMarkers();
        },
        showDrop: function() {
            this.filterMarkers();
        },
        showRoutes: function() {
            this.filterMarkers();
        },
        order: function() {
            this.filterMarkers();
        }
    }
};
</script>
